<template>
  <div class="mt-6 space-y-6">
    <div v-for="config in displayConfigOptions" :key="config.key">
      <div class="flex items-center justify-between brand-input">
        <div :class="{ 'pr-4 max-w-3xl': !!config.note }">
          <p class="subtitle font-semibold" v-html="config.description"></p>
        </div>
        <el-tooltip :disabled="!config.tooltip" :content="config.tooltip">
          <el-switch
            :class="{ 'flex-shrink-0 mt-1': !!config.note }"
            :disabled="config.disabled"
            :value="orgProviderSettings[config.key]"
            @change="(value) => updateOrgProviderSettings({ [config.key]: value })"
            :active-text="config.activeText"
            :inactive-text="config.inactiveText"
          ></el-switch>
        </el-tooltip>
      </div>
      <div
        v-if="config.note"
        class="space-y-2 mt-6 mb-2 subtitle text-sm -ml-1 border-l-4 bg-brand-light-sand py-2 border-brand-sand px-2 rounded-md leading-6"
        v-html="config.note"
      ></div>
    </div>
  </div>
</template>
<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { SET_ORG_DETAILS } from '@/store/mutation-types';
import OrgService from '@/api/org.service';
import { PROVIDERS_WITH_IMMEDIATE_CANCEL } from '@/store/shared';

export default Vue.extend({
  name: 'OrgPaymentProviderConfig',
  data() {
    return {
      savingProviderSettings: false,
    };
  },
  computed: {
    ...mapGetters(['currentOrgSettingsSaveState', 'orgProviderSettings']),
    provider() {
      return this.$store.getters.providerWithVersion;
    },
    configOptions() {
      return [
        {
          key: 'planChangeProrate',
          description: 'Apply proration when customers switch their plan.',
          activeText: 'Proration',
          inactiveText: 'No Proration',
          providers: ['stripe', 'chargebee', 'paddle_billing', 'braintree', 'paddle'],
        },
        {
          key: 'cancelImmediate',
          description: 'Cancel subscriptions at the end of the billing term (default), or immediately',
          activeText: 'Immediate',
          inactiveText: 'End of Term',
          providers: ['stripe', 'chargebee', 'paddle_billing'],
        },
        {
          key: 'unpauseOnCancel',
          description:
            'During subscription cancellation, also <a class="text-brand-blue underline" href="https://stripe.com/docs/billing/subscriptions/pause#unpausing" target="_blank">unpause the subscription</a> if it is currently paused.',
          activeText: 'Unpause',
          inactiveText: 'Leave as is',
          providers: ['stripe'],
        },
        {
          key: 'planChangeImmediate',
          description: 'Set plan changes to occur immediately or at the end of the current billing cycle.',
          activeText: 'Immediate',
          inactiveText: 'End of Term',
          providers: ['chargebee', 'paddle_billing'],
        },
        {
          key: 'pauseEndOfTerm',
          description:
            'Set <a class="text-brand-blue underline" href="https://docs.churnkey.co/pause-wall" target="_blank">Pause Wall</a> to show immediately or at the end of the current billing cycle.',
          activeText: 'End of Term',
          inactiveText: 'Immediate',
          providers: ['stripe'],
          note: '<p><span class="font-semibold">Note:</span> If Pause Wall is set to trigger at "End of Term", this will still set the <strong><code>pause_collection</code></strong> behavior immediately. However, we will add metadata to hide the Pause Wall until the end of the current billing period.</p><p><a href="https://docs.churnkey.co/faqs#64ba34598a944da7b3b30fc0d54eb758" target="_blank" class="text-brand-blue font-semibold">More Details →</a></p>',
        },
        {
          key: 'pauseEndOfTerm',
          description: 'Set subscription pauses to occur at the end of the current billing cycle.',
          activeText: 'End of Term',
          inactiveText: 'Immediate',
          providers: ['chargebee', 'paddle_billing'],
        },
        {
          key: 'annualPauseExtendTerm',
          description: 'Allow annual subscribers to pause their subscription. This will simultaneously extend their term date.',
          activeText: 'Allow',
          inactiveText: 'Disallow',
          providers: ['chargebee', 'paddle_billing'],
          tooltip: 'To enable this feature, please ensure "Immediate" is selected for pauses in the previous setting.',
          disabled: this.orgProviderSettings.pauseEndOfTerm,
        },
      ];
    },
    displayConfigOptions() {
      return this.configOptions.filter((option) => option.providers.includes(this.provider));
    },
  },
  methods: {
    async updateOrgProviderSettings(update) {
      this.savingProviderSettings = true;
      if (update.pauseEndOfTerm && this.orgProviderSettings.annualPauseExtendTerm) {
        update.annualPauseExtendTerm = false;
        this.$notify({
          title: 'Annual Pause Extension Disabled',
          message: 'To enable pauses for annual subscriptions, please ensure "Immediate" mode is selected for pauses.',
          type: 'warning',
        });
      }
      const newProviderSettings = { ...this.orgProviderSettings, ...update };
      this.$store.commit(SET_ORG_DETAILS, { providerSettings: newProviderSettings });
      if (!this.$store.getters.hasPermission('cf:write')) return;
      await OrgService.update(this.$store.state.org._id, { providerSettings: newProviderSettings });
    },
  },
});
</script>
